<template>
    <tr :key="row.id" class="da-row p-2">
        <!-- Postal code -->
        <td class="da-cell">
            <TextInput ref="postalCodeInput" v-model="row.postalCodes" />
        </td>

        <!-- Minimum order size -->
        <td class="da-cell da-cell-currency">
            <div class="da-currency-field">
                <span class="da-currency-affix">CHF</span>
                <NumberInput
                    ref="minOrderSizeInput"
                    v-model="row.minimumOrderSize"
                    :options="{
                        min: 0.00,
                        required: false,
                        step: 0.01,
                        decimals: 2,
                        hideButtons: true,
                    }"
                />
            </div>
        </td>

        <!-- Delivery fee -->
        <td class="da-cell da-cell-currency">
            <div class="da-currency-field">
                <span class="da-currency-affix">CHF</span>
                <NumberInput
                    ref="deliveryFeeInput"
                    v-model="row.deliveryFee"
                    :options="{
                        min: 0.00,
                        required: false,
                        step: 0.01,
                        decimals: 2,
                        hideButtons: true,
                    }"
                />
            </div>
        </td>

        <!-- Free delivery from -->
        <td class="da-cell da-cell-currency">
            <div class="da-currency-field">
                <span class="da-currency-affix">CHF</span>
                <NumberInput
                    ref="freeDeliveryFromInput"
                    v-model="row.freeDeliveryFrom"
                    :options="{
                        min: 0.00,
                        required: false,
                        step: 0.01,
                        decimals: 2,
                        hideButtons: true,
                    }"
                />
            </div>
        </td>

        <!-- right "insert row after" + remove -->
        <td class="da-col-add da-col-add-right">
            <button
                type="button"
                class="btn btn-success"
                @click="insertRow"
                style="width: 2.5rem; height: 2.5rem; padding: 0px !important; margin-end: 12px"
            >
                <i class="la la-plus" style="padding: 0px"></i>
            </button>

            <a @click="removeRow" class="btn btn-sm btn-danger btn-icon btn-icon-md mr-2"
                ><i class="la la-trash"></i
            ></a>
        </td>
    </tr>
</template>

<script setup lang="ts">
    import type { IDeliveryAreaToEdit } from "@/data/model/Customer";
    import { ref } from "vue";

    import NumberInput from "@/portal/components/elements/NumberInput.vue";
    import TextInput from "@/portal/components/elements/TextInput.vue";

    const props = defineProps<{
        index: number;
        row: IDeliveryAreaToEdit;
    }>();

    const row = ref<IDeliveryAreaToEdit>(props.row);

    const emit = defineEmits(["removeRow", "insertRow"]);

    const postalCodeInput = ref<InstanceType<typeof TextInput> | null>(null);
    const minOrderSizeInput = ref<InstanceType<typeof TextInput> | null>(null);
    const deliveryFeeInput = ref<InstanceType<typeof TextInput> | null>(null);
    const freeDeliveryFromInput = ref<InstanceType<typeof TextInput> | null>(null);

    function removeRow() {
        emit("removeRow", props.index);
    }

    function insertRow() {
        emit("insertRow", props.index);
    }

    function validate(): boolean {
        postalCodeInput.value!.setError(false);
        minOrderSizeInput.value!.setError(false);
        deliveryFeeInput.value!.setError(false);
        freeDeliveryFromInput.value!.setError(false);

        let hasError = false;
        if (row.value.postalCodes == null || row.value.postalCodes!.length === 0) {
            postalCodeInput.value!.setError(true);
            hasError = true;
        }

        if (row.value.minimumOrderSize != null && row.value.minimumOrderSize < 0) {
            minOrderSizeInput.value!.setError(true);
            hasError = true;
        }

        if (row.value.deliveryFee != null && row.value.deliveryFee < 0) {
            deliveryFeeInput.value!.setError(true);
            hasError = true;
        }

        if (row.value.freeDeliveryFrom != null && row.value.freeDeliveryFrom < 0) {
            freeDeliveryFromInput.value!.setError(true);
            hasError = true;
        }

        if (row.value.minimumOrderSize != null || row.value.freeDeliveryFrom != null) {
            if ((row.value.minimumOrderSize ?? 0) > (row.value.freeDeliveryFrom ?? 0)) {
                freeDeliveryFromInput.value!.setError(true);
                minOrderSizeInput.value!.setError(true);
                hasError = true;
            }
        }

        return !hasError;
    }
    function getRow(): IDeliveryAreaToEdit {
        return row.value!;
    }

    defineExpose({
        validate,
        getRow,
    });
</script>

<style scoped lang="scss">
    .da-row td {
        border-bottom: 1px solid var(--da-border);
        padding: 10px 12px;
        vertical-align: top;
    }

    .da-row:last-child td {
        border-bottom: none;
    }

    .da-col-add {
        width: 40px;
        padding: 10px 4px !important;
        text-align: center;
        white-space: nowrap;
    }

    .da-col-add-right {
        display: table-cell;
    }

    .da-cell {
        min-width: 190px;
    }

    .da-cell-currency {
        min-width: 160px;
    }

    .da-input {
        width: 100%;
        box-sizing: border-box;
        padding: 8px 10px;
        border: 1px solid var(--da-border);
        border-radius: 8px;
        font-size: 0.9rem;
        color: var(--da-text);
    }

    .da-currency-field {
        display: flex;
        align-items: stretch;
        border: 1px solid var(--da-border);
        border-radius: 8px;
        overflow: hidden;
    }

    .da-currency-affix {
        display: flex;
        align-items: center;
        padding: 0 8px;
        background: var(--da-bg-head);
        color: var(--da-muted);
        font-size: 0.8rem;
        border-right: 1px solid var(--da-border);
    }

    .da-input-currency {
        border: none;
        border-radius: 0;
        text-align: right;
    }
</style>
